/**
 * GlobalEventCreatePage - Global Event Creation with Club Selection
 * 
 * Enhanced event creation form that allows club leaders to create events
 * from anywhere in the app by first selecting which club to create the event for.
 * 
 * This page shows only clubs where the user has admin or owner privileges.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCreateEvent } from '../hooks/useEvents';
import { eventsApi } from '../lib/api/events';
import type { ClubForEventCreation } from '../lib/api/events';
import { TopBar, Button, EmptyState, DurationInput, DistanceInput, HeroImageUpload } from '../components';
import EventVisibilityToggle from '../components/EventVisibilityToggle';
import { Calendar, MapPin, Clock, Users, AlertTriangle, ChevronDown, Image as ImageIcon } from 'lucide-react';
import type { ApiError } from '../lib/api';

interface FormData {
  selectedClubSlug: string;
  title: string;
  description: string;
  starts_at: string;
  duration_minutes: number; // Duration in minutes instead of end time
  distance_km: number; // Optional distance in kilometers
  location_name: string;
  location_address: string;
  visibility: 'public' | 'members_only';
  check_ins_enabled: boolean;
  checkin_radius_m: number;
  checkin_window_before_min: number;
  checkin_window_after_min: number;
}

export default function GlobalEventCreatePage() {
  const navigate = useNavigate();
  
  // Get clubs where user can create events
  const { data: clubsData, isLoading: clubsLoading, error: clubsError } = useQuery({
    queryKey: ['clubs-for-event-creation'],
    queryFn: () => eventsApi.getClubsForEventCreation(),
  });
  
  // Hero image state - will be uploaded after event creation
  const [selectedHeroImage, setSelectedHeroImage] = useState<File | null>(null);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    selectedClubSlug: '',
    title: '',
    description: '',
    starts_at: '',
    duration_minutes: 120, // Default to 2 hours
    distance_km: 0, // Default to no distance
    location_name: '',
    location_address: '',
    visibility: 'public',
    check_ins_enabled: true,
    checkin_radius_m: 150,
    checkin_window_before_min: 30,
    checkin_window_after_min: 30,
  });
  
  const [validationError, setValidationError] = useState<string | null>(null);

  // Get selected club data
  const selectedClub = clubsData?.clubs.find(club => club.slug === formData.selectedClubSlug);
  
  // Event creation mutation - only enabled if club is selected
  const createEvent = useCreateEvent(selectedClub?.id || '');

  // Calculate end time from start time and duration
  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    if (!startTime || durationMinutes <= 0) return '';
    
    const startDate = new Date(startTime);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    return endDate.toISOString().slice(0, 16);
  };

  // Get calculated end time for display and validation
  const calculatedEndTime = calculateEndTime(formData.starts_at, formData.duration_minutes);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!selectedClub) {
      setValidationError('Please select a club to create the event for');
      return;
    }
    
    // Enhanced validation
    if (!formData.title.trim()) {
      setValidationError('Event title is required');
      return;
    }
    
    if (!formData.starts_at) {
      setValidationError('Start date/time is required');
      return;
    }
    
    if (formData.duration_minutes < 15) {
      setValidationError('Event duration must be at least 15 minutes');
      return;
    }
    
    if (formData.duration_minutes > 24 * 60) {
      setValidationError('Event duration cannot exceed 24 hours');
      return;
    }
    
    // Validate dates
    // Validate dates
    const startsAt = new Date(formData.starts_at);
    const now = new Date();
    
    if (startsAt <= now) {
      setValidationError('Event must start in the future');
      return;
    }
    
    setValidationError(null);
    
    // Client-side validation: force members_only for locked clubs (Issue #427)
    const isLockedClub = selectedClub?.visibility === 'private' || selectedClub?.visibility === 'invite_only';
    const finalVisibility = isLockedClub ? 'members_only' : formData.visibility;
    
    // Convert form data to API request format
    const createRequest = {
      title: formData.title,
      description: formData.description || undefined,
      starts_at: Math.floor(new Date(formData.starts_at).getTime() / 1000),
      ends_at: Math.floor(new Date(calculatedEndTime).getTime() / 1000),
      location_name: formData.location_name || undefined,
      location_address: formData.location_address || undefined,
      visibility: finalVisibility,
      distance_km: formData.distance_km > 0 ? formData.distance_km : undefined,
      check_ins_enabled: formData.check_ins_enabled,
      checkin_radius_m: formData.checkin_radius_m,
      checkin_window_before_min: formData.checkin_window_before_min,
      checkin_window_after_min: formData.checkin_window_after_min,
    };
    
    // Create event using mutation
    createEvent.mutate(createRequest, {
      onSuccess: async (response) => {
        const eventId = response.event.id;
        
        // If hero image was selected, upload it after event creation
        if (selectedHeroImage && eventId) {
          try {
            await eventsApi.uploadEventHeroImage(eventId, selectedHeroImage);
          } catch (error) {
            console.warn('Failed to upload hero image:', error);
            // Still navigate to event page even if image upload fails
          }
        }
        
        // Navigate to the new event page
        navigate(`/event/${eventId}`);
      },
    });
  };

  if (clubsLoading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Create Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">Loading your clubs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (clubsError || !clubsData?.clubs) {
    const errorMessage = clubsError 
      ? 'Failed to load clubs'
      : 'No clubs available';
      
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Create Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              icon="📅"
              title="Unable to create event"
              message={errorMessage}
              actionLabel="Back to Home"
              onAction={() => navigate('/')}
            />
          </div>
        </div>
      </div>
    );
  }

  // If user has no clubs where they can create events
  if (clubsData.clubs.length === 0) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Create Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              icon="👑"
              title="No clubs to manage"
              message="You need to be a club leader or owner to create events. Join a club as a leader to get started."
              actionLabel="Browse Clubs"
              onAction={() => navigate('/clubs')}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title="Create Event" 
        onBack={() => navigate(-1)} 
      />
      
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} className="px-4 py-6 space-y-6">
          {/* Club Selection */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-text-muted" />
              <span className="text-text-secondary text-sm">Select club for event</span>
            </div>
            
            <div className="relative">
              <select
                value={formData.selectedClubSlug}
                onChange={(e) => setFormData({ ...formData, selectedClubSlug: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent appearance-none cursor-pointer"
                required
              >
                <option value="">Choose a club...</option>
                {clubsData.clubs.map((club: ClubForEventCreation) => (
                  <option key={club.id} value={club.slug}>
                    {club.name} ({club.member_count} members) - {club.my_role}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-5 h-5 text-text-muted absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Selected Club Display */}
            {selectedClub && (
              <div className="mt-3 p-3 bg-oro-surface-alt border border-oro-border rounded-lg">
                <div className="flex items-center gap-3">
                  {selectedClub.logo_url && (
                    <img 
                      src={selectedClub.logo_url} 
                      alt={`${selectedClub.name} logo`}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-text-primary">{selectedClub.name}</h3>
                    <p className="text-text-muted text-sm">
                      {selectedClub.member_count} members • You are {selectedClub.my_role}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {(validationError || createEvent.error) && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">
                {validationError || ((createEvent.error as unknown as ApiError)?.message || 'Failed to create event')}
              </p>
            </div>
          )}

          {/* Event Details */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-text-primary">Event Details</h3>
            
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-text-secondary mb-2">
                Event Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Morning Trail Run"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                rows={3}
                placeholder="Join us for an energizing trail run through scenic paths..."
              />
            </div>
          </div>

          {/* Hero Image Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-text-muted" />
              <h3 className="font-semibold text-text-primary">Event Photo</h3>
            </div>
            
            <p className="text-sm text-text-secondary">
              Upload a photo for this event. If no photo is uploaded, your club's photo will be used.
            </p>
            
            <HeroImageUpload
              currentImageUrl={heroImageUrl}
              onImageChange={setHeroImageUrl}
              onImageUpload={async (file) => {
                // Store file for upload after event creation
                setSelectedHeroImage(file);
                
                // Create preview URL for immediate UI feedback
                const previewUrl = URL.createObjectURL(file);
                setHeroImageUrl(previewUrl);
                
                return previewUrl;
              }}
              onImageRemove={async () => {
                setSelectedHeroImage(null);
                setHeroImageUrl(null);
                
                // Clean up preview URL
                if (heroImageUrl && heroImageUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(heroImageUrl);
                }
              }}
              enableCrop={true}
              className="w-full"
              disabled={createEvent.isPending}
            />
          </div>

          {/* Event Visibility (Issue #427) */}
          <EventVisibilityToggle
            value={formData.visibility}
            onChange={(visibility) => setFormData({ ...formData, visibility })}
            clubVisibility={selectedClub?.visibility}
          />

          {/* DateTime Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-text-muted" />
              <h3 className="font-semibold text-text-primary">Date & Time</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="starts_at" className="block text-sm font-medium text-text-secondary mb-2">
                  Date & Start Time *
                </label>
                <input
                  type="datetime-local"
                  id="starts_at"
                  value={formData.starts_at}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                  className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <DurationInput
                  label="Duration *"
                  value={formData.duration_minutes}
                  onChange={(minutes) => setFormData({ ...formData, duration_minutes: minutes })}
                  placeholder="e.g. 2h, 1h 30m"
                  minMinutes={15}
                  maxMinutes={24 * 60}
                />
              </div>
            </div>

            {/* Calculated End Time Display */}
            {formData.starts_at && formData.duration_minutes > 0 && (
              <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-text-muted" />
                  <span className="text-text-secondary text-sm">
                    Event ends at: {(() => {
                      const endDate = new Date(calculatedEndTime);
                      return endDate.toLocaleString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      });
                    })()}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Distance Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="font-semibold text-text-primary">Distance</h3>
              <span className="text-xs text-text-muted">(Optional)</span>
            </div>
            
            <DistanceInput
              label=""
              value={formData.distance_km}
              onChange={(distanceKm) => setFormData({ ...formData, distance_km: distanceKm })}
              placeholder="e.g. 5.2"
            />
          </div>

          {/* Location Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-text-muted" />
              <h3 className="font-semibold text-text-primary">Location</h3>
            </div>
            
            <div>
              <label htmlFor="location_name" className="block text-sm font-medium text-text-secondary mb-2">
                Location Name
              </label>
              <input
                type="text"
                id="location_name"
                value={formData.location_name}
                onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Central Park Entrance"
              />
            </div>

            <div>
              <label htmlFor="location_address" className="block text-sm font-medium text-text-secondary mb-2">
                Full Address <span className="text-text-muted">(optional)</span>
              </label>
              <input
                type="text"
                id="location_address"
                value={formData.location_address}
                onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Central Park, New York, NY 10024"
              />
            </div>
          </div>

          {/* Check-in Settings */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-text-primary">Check-in Settings</h3>
            
            {/* Check-ins Toggle */}
            <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.check_ins_enabled}
                  onChange={(e) => setFormData({ ...formData, check_ins_enabled: e.target.checked })}
                  className="w-5 h-5 text-accent bg-oro-surface border-oro-border rounded mt-0.5 focus:ring-accent focus:ring-2"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-text-primary">Enable check-ins</span>
                  <p className="text-xs text-text-muted mt-1">
                    Allow members to check in when they arrive at the event
                  </p>
                </div>
              </label>
            </div>
            
            {/* Check-in Parameters - Collapsible */}
            {formData.check_ins_enabled && (
              <div className="space-y-4 border-t border-oro-border pt-4">
                <div>
                  <label htmlFor="checkin_radius_m" className="block text-sm font-medium text-text-secondary mb-2">
                    Check-in Radius (meters)
                  </label>
                  <select
                    id="checkin_radius_m"
                    value={formData.checkin_radius_m}
                    onChange={(e) => setFormData({ ...formData, checkin_radius_m: parseInt(e.target.value, 10) })}
                    className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value={50}>50m - Indoor venue</option>
                    <option value={100}>100m - Small outdoor area</option>
                    <option value={150}>150m - Default</option>
                    <option value={300}>300m - Large park/area</option>
                    <option value={500}>500m - Very large area</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="checkin_window_before_min" className="block text-sm font-medium text-text-secondary mb-2">
                      Before (min)
                    </label>
                    <select
                      id="checkin_window_before_min"
                      value={formData.checkin_window_before_min}
                      onChange={(e) => setFormData({ ...formData, checkin_window_before_min: parseInt(e.target.value, 10) })}
                      className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="checkin_window_after_min" className="block text-sm font-medium text-text-secondary mb-2">
                      After (min)
                    </label>
                    <select
                      id="checkin_window_after_min"
                      value={formData.checkin_window_after_min}
                      onChange={(e) => setFormData({ ...formData, checkin_window_after_min: parseInt(e.target.value, 10) })}
                      className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="space-y-3 pb-8">
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={createEvent.isPending || !selectedClub}
            >
              {createEvent.isPending ? 'Creating Event...' : 'Create Event'}
            </Button>
            
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => navigate(-1)}
              disabled={createEvent.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}